import React, { createContext, useContext, useEffect, useMemo } from 'react';
import { useQueryCustom } from 'hooks';
import { IVR_CALLS_FOR_PATIENT } from 'gql';
import { CompletedStatus } from 'types.d';
import {
  IvrCallStatusSummary,
  IvrCallsForPatientQuery,
  IvrCallsForPatientQueryVariables,
  IvrSurveyPhase,
  IVR_LIVE_PHASES,
} from 'modules/patients/interfaces';

/**
 * Recover a call's real outcome when the backend has flattened it to OPTED_OUT.
 *
 * `patientOptedOut` is resolved once per patient and checked before any
 * per-call state, so as soon as doNotCall is set every call this patient ever
 * had comes back OPTED_OUT -- including surveys that completed months earlier.
 * Left alone that would relabel finished history and turn a completed survey's
 * chip grey. The opt-out is a property of the patient, not of a past call, and
 * is surfaced as such on the patient header.
 *
 * Only what the call itself recorded is used to recover the outcome. A survey
 * finalized before the backend started setting completedStatus reads as PARTIAL
 * rather than COMPLETED -- imprecise, but far closer than OPTED_OUT.
 */
const resolveCall = (call: IvrCallStatusSummary): IvrCallStatusSummary => {
  if (call?.phase !== IvrSurveyPhase.OptedOut) {
    return call;
  }

  const answered = call.answeredCount || 0;

  if (call.completedStatus === CompletedStatus.Totally) {
    return {
      ...call,
      phase: IvrSurveyPhase.Completed,
      description: `Survey completed by phone with ${answered} answer(s).`,
    };
  }

  if (answered > 0) {
    return {
      ...call,
      phase: IvrSurveyPhase.Partial,
      description: `Call ended early. ${answered} answer(s) were saved.`,
    };
  }

  // Nothing was ever collected, so OPTED_OUT is the honest outcome.
  return call;
};

/** A call lasts one to three minutes and answeredCount climbs throughout. */
const POLL_INTERVAL_MS = 8000;

/** Server caps at 100. A patient past that loses chips on the oldest results. */
const MAX_CALLS = 100;

type IvrCallsContextValue = {
  /** Newest call per survey result. Drives the chip on a survey row. */
  callsBySurveyResult: Map<string, IvrCallStatusSummary>;
  /**
   * Every call context, most recent first, including superseded attempts.
   * Retries get a new callSid and therefore a new context, so the map above
   * hides earlier attempts -- which is exactly what a history needs to show.
   */
  allCalls: IvrCallStatusSummary[];
  loading: boolean;
};

const IvrCallsContext = createContext<IvrCallsContextValue>({
  callsBySurveyResult: new Map(),
  allCalls: [],
  loading: false,
});

type Props = {
  patientId?: string | null;
};

/**
 * Fetches a patient's phone-survey calls once and indexes them by survey
 * result, so every row in the survey list can look itself up for free.
 *
 * This is also what tells us a result *is* a phone survey. `SurveyResultEmbedded`
 * carries no `source`, and `ivrCallStatusBySurveyResult` has no source guard --
 * it returns a fabricated DIALING summary for an unopened SMS survey. A result
 * only has a call context if a call was actually placed for it, so presence in
 * this map is the reliable test.
 */
export const IvrCallsProvider: React.FC<Props> = ({ patientId, children }) => {
  const { data, loading, startPolling, stopPolling } = useQueryCustom<
    IvrCallsForPatientQuery,
    IvrCallsForPatientQueryVariables
  >({
    api: IVR_CALLS_FOR_PATIENT,
    skip: !patientId,
    variables: patientId
      ? { params: { patientId, limit: MAX_CALLS } }
      : undefined,
  });

  const calls = useMemo(
    () => (data?.ivrCallsForPatient || []).map(resolveCall),
    [data],
  );

  const value = useMemo<IvrCallsContextValue>(() => {
    const callsBySurveyResult = new Map<string, IvrCallStatusSummary>();
    calls.forEach(call => {
      // Server returns most recent first; keep the newest per survey result.
      if (
        call?.surveyResultId &&
        !callsBySurveyResult.has(call.surveyResultId)
      ) {
        callsBySurveyResult.set(call.surveyResultId, call);
      }
    });
    return { callsBySurveyResult, allCalls: calls, loading };
  }, [calls, loading]);

  const hasLiveCall = useMemo(
    () =>
      calls.some(call => !!call?.phase && IVR_LIVE_PHASES.includes(call.phase)),
    [calls],
  );

  // Poll only while something is actually in flight, and stop on unmount.
  useEffect(() => {
    if (!hasLiveCall) {
      stopPolling();
      return undefined;
    }
    startPolling(POLL_INTERVAL_MS);
    return () => stopPolling();
    // startPolling/stopPolling are not stable across renders in this Apollo
    // version; depending on them would restart the interval every render.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [hasLiveCall]);

  return (
    <IvrCallsContext.Provider value={value}>
      {children}
    </IvrCallsContext.Provider>
  );
};

/** The call for one survey result, or undefined when it was not a phone survey. */
export const useIvrCall = (
  surveyResultId?: string | null,
): IvrCallStatusSummary | undefined => {
  const { callsBySurveyResult } = useContext(IvrCallsContext);
  return surveyResultId ? callsBySurveyResult.get(surveyResultId) : undefined;
};

/**
 * Every call attempt for the patient, most recent first -- superseded retries
 * included. This is the one to use for history; `callsBySurveyResult` keeps
 * only the newest per result and would under-report how often we rang.
 */
export const useIvrCalls = (): {
  calls: IvrCallStatusSummary[];
  loading: boolean;
} => {
  const { allCalls, loading } = useContext(IvrCallsContext);
  return { calls: allCalls, loading };
};

export default IvrCallsContext;
